import { BaseTimelineLabelsGL } from "./TimelineLabels/BaseTimelineLabelsGL";
import { TimelineThemeGL } from "./TimelineThemeGL";
import { RowPainter } from "./RowPainter";
import { Camera } from "./Camera";
import { TimeAxisLines } from "./TimelineLabels/TimeAxisLines";
import { ColPainter } from "./ColPainter";
import { INonFlyingAssignment, IDisplayPairing, IDisplayBidPairing, IDisplayBidNFA } from "@vmosolutions/oc-interfaces";
import { ScrollBarWrapper } from "./ScrollBar/ScrollBarWrapper";
import { IBaseTime, ICrewScheduleBaseEvent, IDayLogicModifierMap } from "../redux/reducers/CrewScheduleReducer";
import { IBiddingNFACounts } from "src/redux/actions/Bidding.Actions";
import { IAirportOptionMap } from "src/redux/reducers/StructuresReducer";

export class BaseTimelineGL {
  protected mount: HTMLElement;
  public canvas: HTMLCanvasElement;
  public loseContextExt: WEBGL_lose_context | null | undefined;
  protected gl: WebGLRenderingContext | null;
  protected camera: Camera;
  protected timelineLabelsGL: BaseTimelineLabelsGL;
  protected timeAxisLines: TimeAxisLines;

  protected theme: TimelineThemeGL;
  protected rowPainter: RowPainter;
  protected colPainter: ColPainter;
  protected maxHeight: number;
  protected minRowIndex: number;
  protected maxRowIndex: number;

  protected isICAO: boolean | undefined;
  protected airportOptions: IAirportOptionMap | undefined;

  public hoveredTime: number | null = null;

  public static timelineStart = 60;
  public static NFAcountRowHeight = 24; // this static value is multiplied by the amount of selected nfa types to display.
  public static biddingWindowBarHeight = 10; // Height of the bidding block bar display above crew sched timeline.

  protected baseTime: IBaseTime;

  // If crew schedule has an active bidding mode set, this object will contain info on nfa types and their assignment counts.
  public biddingNFACounts: IBiddingNFACounts | undefined;
  private biddingNFATypeSelectedCount: number;

  constructor(mount: HTMLElement, width: number, height: number) {
    this.mount = mount;
    this.canvas = document.createElement("canvas");
    const devicePixelRatio = window.devicePixelRatio || 1;
    this.canvas.width = width * devicePixelRatio;
    this.canvas.height = height * devicePixelRatio;
    this.isICAO = false;
    this.airportOptions = {};
    this.biddingNFACounts = undefined;
    this.hoveredTime = 0;

    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.gl = this.canvas.getContext("webgl");
    this.loseContextExt = this.gl.getExtension("WEBGL_lose_context");
    if (!this.loseContextExt) {
      console.error("WEBGL_lose_context is not supported");
    }
    if (!this.gl) {
      throw new Error("Unable to initialize WebGL. Your browser or machine may not support it.");
    }
    this.gl.enable(this.gl.DEPTH_TEST);
    this.gl.enable(this.gl.BLEND);
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
    this.mount.appendChild(this.canvas);

    this.camera = new Camera([-width / 2.0, width / 2.0], [0, height]);
    this.rowPainter = new RowPainter(this.gl);
    this.colPainter = new ColPainter(this.gl);
    this.theme = new TimelineThemeGL();

    this.timelineLabelsGL = new BaseTimelineLabelsGL(mount, width, height);
    this.timeAxisLines = new TimeAxisLines(this.gl);
    this.maxHeight = 0;
    this.minRowIndex = 0;
    this.maxRowIndex = 0;

    this.baseTime = {
      isUTC: true,
    };

    this.biddingNFATypeSelectedCount = 0;
  }

  // Function used to return total height of selected NFA types.
  public returnNFARowHeight(): number {
    return !this.biddingNFACounts
      ? 0
      : this.biddingNFATypeSelectedCount * BaseTimelineGL.NFAcountRowHeight + BaseTimelineGL.biddingWindowBarHeight;
  }

  /**
   * Use passed in information for selected NFA Types and their counts.
   * Store information pertaining to Selected NFA type counts and pass to timelineLabelsGL.
   * @param NFACounts -
   */
  public setBidPackageNFACounts(NFACounts: IBiddingNFACounts | undefined): void {
    this.biddingNFACounts = NFACounts;
    if (NFACounts) {
      const selectedNFACount = Object.values(Object.values(NFACounts)?.[0])?.length;
      const assignedNFACount = selectedNFACount > 0 ? selectedNFACount : 1; // If bidding is active, set mininum height to 1 row;
      this.biddingNFATypeSelectedCount = assignedNFACount; // return count of "selected" NFA types.
      this.timelineLabelsGL.setBiddingNFARowHeight(
        assignedNFACount * BaseTimelineGL.NFAcountRowHeight + BaseTimelineGL.biddingWindowBarHeight
      );
    } else {
      this.biddingNFATypeSelectedCount = 0;
      this.timelineLabelsGL.setBiddingNFARowHeight(0);
    }
  }

  public setBaseTime(baseTime: IBaseTime): void {
    this.baseTime = baseTime;
  }

  public setIsICAO(isICAO: boolean): void {
    this.isICAO = isICAO;
  }

  protected updateCameraRange(): void {
    const [minY, maxY] = this.camera.getYRange();
    const delta = maxY - minY;

    let [newMinY, newMaxY] = [minY, maxY];
    if (minY < 0) {
      newMinY = 0;
      newMaxY = newMinY + delta;
    } else if (maxY > this.maxHeight) {
      const timelineHeight =
        this.canvas.height / window.devicePixelRatio - (BaseTimelineGL.timelineStart + this.returnNFARowHeight());
      newMaxY = this.maxHeight >= timelineHeight ? Math.min(maxY, this.maxHeight) : timelineHeight;
      const tempMin = newMaxY - delta;
      newMinY = minY >= 0 ? tempMin : 0;
    }
    this.camera.setYRange([newMinY, newMaxY]);
  }

  public setViewport(width: number, height: number): void {
    const devicePixelRatio = window.devicePixelRatio || 1;
    const scaledWidth = width * devicePixelRatio;
    const scaledHeight = height * devicePixelRatio;

    this.canvas.width = scaledWidth;
    this.canvas.height = scaledHeight;

    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    const scaledTimelineStart = (BaseTimelineGL.timelineStart + this.returnNFARowHeight()) * devicePixelRatio;

    const [minY] = this.camera.getYRange();

    const newMaxY = minY + height - (BaseTimelineGL.timelineStart + this.returnNFARowHeight());
    const newMinY = minY;
    this.camera.setYRange([newMinY, newMaxY]);
    if (this.maxHeight >= height) {
      this.gl?.viewport(0, 0, scaledWidth, Math.abs(scaledHeight - scaledTimelineStart));
      this.timelineLabelsGL.setDimensions(width, height);
    } else {
      // const scaledMaxRange = newMaxY * devicePixelRatio;
      this.gl?.viewport(0, 0, scaledWidth, Math.abs(scaledHeight - scaledTimelineStart));
      this.timelineLabelsGL.setDimensions(width, newMaxY + (BaseTimelineGL.timelineStart + this.returnNFARowHeight()));
    }
    this.updateCameraRange();
  }

  /** getTheme @returns the theme for TimelineGL */
  public getTheme = (): TimelineThemeGL => this.theme;

  /** setTheme @param theme - theme for TimelineGL */
  public setTheme = (theme: any): void => {
    this.theme.updateTheme(theme);
  };

  /**
   * getTimeRange
   * @returns the xRange of the camera
   */
  public getTimeRange = (): number[] => this.camera.getXRange();

  /**
   * setTimeAxis sets the xRange of the camera
   * @param startTime - the start of the xRange for camera
   * @param endTime - the end of the xRange for camera
   */
  public setTimeAxis = (startTime: number, endTime: number): void => {
    this.camera.setXRange([startTime, endTime]);
  };

  public setHoveredTime = (time: number): void => {
    this.hoveredTime = time;
  };

  /**
   * moveCameraByXPixels pans the camera x pixels
   * @param pixels - num pixels to pan camera
   */
  public moveCameraByXPixels = (pixels: number): void => {
    const [minTime, maxTime] = this.camera.getXRange();
    const time = this.pixelCoordsToTimeAxis(pixels);
    // check on isNaN to prevent timeline from breaking when you
    // open a new timeline with a focus events
    if (!isNaN(time)) {
      this.setTimeAxis(minTime + time, maxTime + time);
    }
  };

  /**
   * moveCameraByYPixels  pans the camera vertically by pixels amount
   * @param pixels - the amount to pan the camera vertically
   */
  public moveCameraByYPixels(pixels: number): void {
    const [minY, maxY] = this.camera.getYRange();
    const delta = maxY - minY;

    //dont move the camera up & down if the rows are less than the max height
    if (delta >= this.maxHeight) {
      return;
    }

    if (maxY + pixels >= this.maxHeight) {
      pixels = this.maxHeight - maxY;
    }
    let [newMinY, newMaxY] = [minY + pixels, maxY + pixels];

    if (minY + pixels < 0) {
      newMinY = 0;
      newMaxY = delta;
    } else if (maxY + pixels > this.maxHeight) {
      const timelineHeight = this.canvas.height - (BaseTimelineGL.timelineStart + this.returnNFARowHeight());
      newMaxY = this.maxHeight >= timelineHeight ? Math.min(maxY + pixels, this.maxHeight) : timelineHeight;
      const tempMin = newMaxY - delta;
      newMinY = minY >= 0 ? tempMin : 0;
    }
    this.camera.setYRange([newMinY, newMaxY]);
  }

  public getYRange = (): number[] => this.camera.getYRange();

  /**
   * pixelCoordsToTimeAxis converts from pixels to time using camera
   * @param pixels - the pixel value to convert to time
   */
  public pixelCoordsToTimeAxis = (pixels: number): number => {
    return this.camera.pixelCoordsToTimeAxis(pixels, this.canvas.width);
  };

  /**
   * timeAxisToPixelCoords converts a time value to pixel coords
   * @param time - the time to convert to a pixel value
   */
  public timeAxisToPixelCoords = (time: number): number => {
    return this.camera.timeAxisToPixelCoords(time, this.canvas.width);
  };

  /**
   * pixelCoordsToEpochTime converts from pixels to time using camera
   * @param pixels - the pixel value to convert to epoch time
   */
  public pixelCoordsToEpochTime = (pixels: number): number => {
    return this.camera.pixelCoordsToEpochTime(pixels, this.canvas.width);
  };

  /**
   * zoomCamera zooms the camera by deltaZoom
   * @param deltaZoom - the amount to zoom the camera in or out
   */
  public zoomCamera(deltaZoom: number): void {
    const zoomTime = this.pixelCoordsToTimeAxis(deltaZoom);
    this.camera.zoom(zoomTime);
  }

  public drawNFALabels(
    camera: Camera,
    nonFlyingAssingments: (INonFlyingAssignment | IDisplayBidNFA)[],
    rowStart: number
  ): void {
    this.timelineLabelsGL.drawNFALabels(camera, nonFlyingAssingments, rowStart, this.baseTime);
  }

  public setHideNfaPriority(hide: boolean): void {
    this.timelineLabelsGL.setHideNfaPriority(hide);
  }

  public drawPairingLabels(
    camera: Camera,
    pairings: (IDisplayPairing | IDisplayBidPairing)[],
    rowStart: number,
    drawCheckInOutTimes?: boolean
  ): void {
    this.timelineLabelsGL.drawPairingLabels(camera, pairings, rowStart, this.baseTime, drawCheckInOutTimes);
  }

  public drawRestTime(camera: Camera, events: ICrewScheduleBaseEvent[], rowStart: number): void {
    this.timelineLabelsGL.drawRestTimes(camera, events, rowStart, this.baseTime);
  }

  public drawDayLogicModifiers(camera: Camera, dayLogicModifiers: IDayLogicModifierMap, y: number): void {
    this.timelineLabelsGL.drawTimelineLogicModifiers(camera, dayLogicModifiers, y);
  }

  public draw(): void {
    this.gl?.clearColor(
      this.theme.clearColor.red,
      this.theme.clearColor.green,
      this.theme.clearColor.blue,
      this.theme.clearColor.alpha
    );
    // this.gl.clearColor(0.0, 0.0, 0.2, 1.0);
    this.timelineLabelsGL.clear();
    this.camera.updateProjectionMatrix();

    // Clear the color buffer with specified clear color
    this.gl?.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);
    this.timelineLabelsGL.drawTimeAxis(this.camera, this.theme, this.baseTime, this.biddingNFACounts);

    const currentTimeInPixels = this.camera.timeAxisToPixelCoords(Date.now(), this.canvas.width);
    if (currentTimeInPixels < this.canvas.width - ScrollBarWrapper.scrollBarWidth) {
      this.timelineLabelsGL.drawTimeIndicator(this.theme, currentTimeInPixels);
    }

    const hoveredTimeInPixels = this.camera.timeAxisToPixelCoords(this.hoveredTime, this.canvas.width);
    if (hoveredTimeInPixels < this.canvas.width - ScrollBarWrapper.scrollBarWidth) {
      this.timelineLabelsGL.drawHoveredTimeIndicator(this.theme, hoveredTimeInPixels);
    }

    if (this.gl) {
      this.timeAxisLines.updateBuffers(this.gl, this.getTimeRange(), this.baseTime);
      this.timeAxisLines.draw(this.gl, this.camera, this.theme.timeAxisLineColor);
    }
  }
}
